"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Link from "@/components/Link";
import ExperienceCardV2 from "@/app/(main)/components/ExperienceCardV2";
import type { ExperienceTourV2 } from "@/app/(main)/components/ExperienceCardV2";
import { fetchExperiencesClient } from "@/lib/experiences";
import { AppRoutes } from "@/lib/routeResolver";

// ─── Props ──────────────────────────────────────────────────────────

interface ExperiencesClientProps {
  initialItems: ExperienceTourV2[];
  initialTotalPages: number;
  initialCount: number;
  pageSize?: number;
  sidebarContent?: ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────

export default function ExperiencesClient({
  initialItems,
  initialTotalPages,
  initialCount,
  pageSize = 10,
  sidebarContent,
}: ExperiencesClientProps) {
  const [items, setItems] = useState(initialItems);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [totalCount, setTotalCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const shouldScroll = useRef(false);

  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      const top =
        containerRef.current.getBoundingClientRect().top +
        window.scrollY -
        100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  const loadItems = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        const result = await fetchExperiencesClient(page, pageSize);
        setItems(result.items);
        setTotalPages(result.totalPages);
        setTotalCount(result.count);
      } finally {
        setLoading(false);
        if (shouldScroll.current) {
          shouldScroll.current = false;
          requestAnimationFrame(() => scrollToTop());
        }
      }
    },
    [pageSize, scrollToTop],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadItems(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  function handlePageChange(page: number) {
    shouldScroll.current = true;
    setCurrentPage(page);
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Build pagination entries with stable keys
  const paginationEntries: {
    key: string;
    type: "page" | "ellipsis";
    value: number;
  }[] = [];
  if (totalPages > 1) {
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        paginationEntries.push({ key: `p${i}`, type: "page", value: i });
      }
    } else {
      paginationEntries.push({ key: "p1", type: "page", value: 1 });
      if (currentPage > 3) {
        paginationEntries.push({
          key: "el-start",
          type: "ellipsis",
          value: 0,
        });
      }
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        paginationEntries.push({ key: `p${i}`, type: "page", value: i });
      }
      if (currentPage < totalPages - 2) {
        paginationEntries.push({
          key: "el-end",
          type: "ellipsis",
          value: 0,
        });
      }
      paginationEntries.push({
        key: `p${totalPages}`,
        type: "page",
        value: totalPages,
      });
    }
  }

  return (
    <div ref={containerRef}>
      <div className="row">
        {/* ── Main column ── */}
        <div className="col-xxl-8 col-lg-7">
          <div className="page-single">
            <div className="page-content d-block mb-4">
              <div className="page-meta mt-0 mb-20">
                <span className="page-tag">All Regions</span>
                <span className="ratting ms-2">
                  {totalCount} Experience{totalCount !== 1 ? "s" : ""}
                </span>
              </div>

              <h2 className="box-title mb-2">Explore All Experiences</h2>

              <p className="blog-text">
                Discover unforgettable experiences across our featured regions
                and destinations. Browse cultural journeys, sightseeing tours,
                and once-in-a-lifetime activities all in one place.
              </p>
            </div>

            {/* Top pagination */}
            {totalCount > 0 && (
              <div className="d-flex align-items-center justify-content-between mb-4">
                <p className="blog-text mb-0">
                  Showing{" "}
                  <strong>
                    {startItem}&ndash;{endItem}
                  </strong>{" "}
                  of <strong>{totalCount}</strong> experience
                  {totalCount !== 1 ? "s" : ""}
                </p>
                {totalPages > 1 && (
                  <div className="d-flex gap-2">
                    <button
                      className="th-btn style4"
                      disabled={currentPage <= 1 || loading}
                      onClick={() => handlePageChange(currentPage - 1)}
                      style={{ padding: "4px 10px" }}
                    >
                      <i className="fa fa-arrow-left"></i> Prev
                    </button>
                    <button
                      className="th-btn style4"
                      disabled={currentPage >= totalPages || loading}
                      onClick={() => handlePageChange(currentPage + 1)}
                      style={{ padding: "4px 10px" }}
                    >
                      Next <i className="fa fa-arrow-right"></i>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Tour grid */}
            <div
              className="row g-4"
              style={{
                minHeight: "200px",
                opacity: loading ? 0.5 : 1,
                transition: "opacity 0.2s",
              }}
            >
              {items.length > 0 ? (
                items.map((tour, index) => (
                  <div key={tour.id ?? index} className="col-sm-6">
                    <ExperienceCardV2 tour={tour} index={index} />
                  </div>
                ))
              ) : (
                <div className="col-12 text-center py-5">
                  <div className="mb-4">
                    <i className="fa-light fa-binoculars fa-4x text-theme"></i>
                  </div>
                  <p className="blog-text mb-4">
                    No experiences found at the moment.
                  </p>
                  <Link
                    href={AppRoutes.home()}
                    className="th-btn style1 th-icon"
                    title="Back to Central Holidays home page"
                  >
                    Back To Home
                  </Link>
                </div>
              )}
            </div>

            {/* Bottom pagination */}
            <div
              className="mc-pagination"
              style={{ display: totalPages > 1 ? "flex" : "none" }}
            >
              <button
                className="mc-pagination-btn mc-pagination-prev"
                disabled={currentPage <= 1 || loading}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className="fa fa-chevron-left"></i>
              </button>

              {paginationEntries.map((entry) =>
                entry.type === "ellipsis" ? (
                  <span key={entry.key} className="mc-pagination-ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    key={entry.key}
                    className={`mc-pagination-btn ${currentPage === entry.value ? "mc-pagination-active" : ""}`}
                    onClick={() => handlePageChange(entry.value)}
                    disabled={loading}
                  >
                    {entry.value}
                  </button>
                ),
              )}

              <button
                className="mc-pagination-btn mc-pagination-next"
                disabled={currentPage >= totalPages || loading}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className="fa fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div className="col-xxl-4 col-lg-5">
          <aside
            className="sidebar-area position-sticky style3"
            style={{ top: 25 }}
          >
            {sidebarContent}
          </aside>
        </div>
      </div>
    </div>
  );
}
